import React, {
  cloneElement,
  isValidElement,
  ReactElement,
  ReactNode,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import './tooltip.css';

type Placement = 'top' | 'bottom' | 'left' | 'right';

type TooltipProps = {
  content: ReactNode;
  children: ReactElement;
  placement?: Placement;
  openDelay?: number;
  closeDelay?: number;
  offset?: number;
  maxWidth?: number | string;
  asChild?: boolean;
  interactive?: boolean;
  className?: string;
};

type RectLike = { top: number; left: number; width: number; height: number; right: number; bottom: number };

function ensurePortalRoot(): HTMLElement {
  let el = document.getElementById('tooltip-root');
  if (!el) {
    el = document.createElement('div');
    el.id = 'tooltip-root';
    el.style.position = 'fixed';
    el.style.left = '0';
    el.style.top = '0';
    el.style.zIndex = '10000';
    el.style.pointerEvents = 'none';
    document.body.appendChild(el);
  }
  return el;
}

function pickPosition(anchor: RectLike, preferred: Placement, offset: number): { x: number; y: number; finalPlacement: Placement } {
  const vw = document.documentElement.clientWidth;
  const vh = document.documentElement.clientHeight;

  const tryPos = (p: Placement) => {
    if (p === 'top') return { x: Math.round(anchor.left + anchor.width / 2), y: Math.round(anchor.top - offset), p };
    if (p === 'bottom') return { x: Math.round(anchor.left + anchor.width / 2), y: Math.round(anchor.bottom + offset), p };
    if (p === 'left') return { x: Math.round(anchor.left - offset), y: Math.round(anchor.top + anchor.height / 2), p };
    return { x: Math.round(anchor.right + offset), y: Math.round(anchor.top + anchor.height / 2), p };
  };

  const order = [preferred, ...(preferred === 'top' || preferred === 'bottom' ? ['bottom','right','left','top'] : ['right','top','bottom','left'])]
    .filter((v, i, a) => a.indexOf(v) === i) as Placement[];

  for (const p of order) {
    const { x, y } = tryPos(p);
    if (x >= -8 && x <= vw + 8 && y >= -8 && y <= vh + 8) return { x, y, finalPlacement: p };
  }
  const f = tryPos(preferred);
  return { x: f.x, y: f.y, finalPlacement: preferred };
}

function useIsomorphicLayoutEffect(cb: React.EffectCallback, deps: React.DependencyList) {
  return typeof window !== 'undefined' ? useLayoutEffect(cb, deps) : useEffect(cb, deps);
}

export function TooltipProvider({ children }: { children: ReactNode }) {
  useEffect(() => { ensurePortalRoot(); }, []);
  return <>{children}</>;
}

export default function Tooltip({
  content,
  children,
  placement = 'top',
  openDelay = 180,
  closeDelay = 80,
  offset = 10,
  maxWidth = 320,
  asChild = false,
  interactive = false,
  className,
}: TooltipProps) {
  const anchorRef = useRef<HTMLElement | SVGElement | null>(null);
  const tipRef = useRef<HTMLDivElement | null>(null);
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState<{ left: number; top: number; placement: Placement }>({ left: 0, top: 0, placement });
  const openTo = useRef<number | null>(null);
  const closeTo = useRef<number | null>(null);
  const id = useId();

  const clearTimers = () => {
    if (openTo.current) { window.clearTimeout(openTo.current); openTo.current = null; }
    if (closeTo.current) { window.clearTimeout(closeTo.current); closeTo.current = null; }
  };

  const scheduleOpen = () => {
    clearTimers();
    openTo.current = window.setTimeout(() => setOpen(true), openDelay);
  };
  const scheduleClose = () => {
    clearTimers();
    closeTo.current = window.setTimeout(() => setOpen(false), closeDelay);
  };

  function computeAndSet() {
    const anchor = anchorRef.current;
    const tip = tipRef.current;
    if (!anchor || !tip) return;

    const rect = (anchor as any).getBoundingClientRect?.() as RectLike | undefined;
    if (!rect) return;

    const { x, y, finalPlacement } = pickPosition(rect, placement, offset);

    const vw = document.documentElement.clientWidth;
    const vh = document.documentElement.clientHeight;
    const pad = 8;

    let left = x;
    let top = y;

    const w = tip.offsetWidth;
    const h = tip.offsetHeight;

    if (finalPlacement === 'top') {
      left = x - Math.round(w / 2);
      top = y - h;
    } else if (finalPlacement === 'bottom') {
      left = x - Math.round(w / 2);
      top = y;
    } else if (finalPlacement === 'left') {
      left = x - w;
      top = y - Math.round(h / 2);
    } else { // right
      left = x;
      top = y - Math.round(h / 2);
    }

    left = Math.max(pad, Math.min(left, vw - w - pad));
    top  = Math.max(pad, Math.min(top, vh - h - pad));

    setCoords({ left, top, placement: finalPlacement });
  }

  useIsomorphicLayoutEffect(() => {
    if (!open) return;
    const ro = new ResizeObserver(() => computeAndSet());
    const onScroll = () => computeAndSet();
    const onResize = () => computeAndSet();
    const a = anchorRef.current;
    if (a) try { ro.observe(a as Element); } catch {}
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onResize);
    computeAndSet();
    return () => {
      try { ro.disconnect(); } catch {}
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onResize);
    };
  }, [open, placement, offset, content]);

  useEffect(() => () => clearTimers(), []);

  const portalRoot = typeof window !== 'undefined' ? ensurePortalRoot() : null;

  const childProps: any = {
    ref: (el: HTMLElement | SVGElement | null) => { anchorRef.current = el; },
    onMouseEnter: (e: any) => { (children as any).props?.onMouseEnter?.(e); scheduleOpen(); },
    onMouseLeave: (e: any) => { (children as any).props?.onMouseLeave?.(e); scheduleClose(); },
    onFocus: (e: any) => { (children as any).props?.onFocus?.(e); scheduleOpen(); },
    onBlur: (e: any) => { (children as any).props?.onBlur?.(e); scheduleClose(); },
    onKeyDown: (e: any) => { (children as any).props?.onKeyDown?.(e); if (e.key === 'Escape') setOpen(false); },
    'aria-describedby': open ? id : undefined,
  };

  const anchor = asChild && isValidElement(children)
    ? cloneElement(children, childProps)
    : (
      <span className="tooltip-anchor" {...childProps}>
        {children}
      </span>
    );

  const tip = open && portalRoot ? (
    <div
      id={id}
      role="tooltip"
      ref={tipRef}
      className={`tooltip ${className ?? ''} ${interactive ? 'interactive' : ''} pos-${coords.placement}`}
      style={{ left: coords.left + 'px', top: coords.top + 'px', maxWidth }}
      onMouseEnter={() => clearTimers()}
      onMouseLeave={() => scheduleClose()}
    >
      <div className="tooltip-inner">
        {content}
        <div className="tooltip-arrow" aria-hidden />
      </div>
    </div>
  ) : null;

  return (
    <>
      {anchor}
      {portalRoot && tip ? createPortal(tip, portalRoot) : null}
    </>
  );
}


